// Step 2 of a Properties project: a family, a calculator (those the model does not qualify for are greyed with the
// reason), its generated form with the cost against the plan, the run and its result views.
import { Alert, Anchor, Badge, Button, Grid, Group, NavLink, SegmentedControl, Stack, Text, Tooltip } from "@mantine/core";
import { Link } from "react-router";
import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { ErrorBox, notifyError } from "../../components/Errors";
import { PreviousRuns, RunCard } from "../../components/RunCard";
import { post, type Job, type ProjectDetail } from "../../lib/api";
import { useLiveJob } from "../../lib/hooks";
import { Formula } from "../../components/Formula";
import { changedInputs, chosenEngine, defaultInputs, describeProperties, engineChoices, engineTitle, fmtSeconds, formProblems,
  propertiesCheck, propertiesRegistry, type CalcDesc, type CalcSpec, type PropertiesMemory, type PropertiesPreset, type PropertyResult } from "../../lib/properties";
import { PropertyForm } from "./PropertyForm";
import { PropertyResultView } from "./PropertyResultView";
import { examplesPath } from "../../lib/modules";

export function PropertiesStep({ project, mem, update, remember, onBack }: {
  project: ProjectDetail; mem: PropertiesMemory; update: (m: Partial<PropertiesMemory>) => void;
  remember: (m: Partial<PropertiesMemory>) => void; onBack: () => void;
}) {
  const qc = useQueryClient();
  const reg = useQuery({ queryKey: ["properties-registry", project.id], queryFn: () => propertiesRegistry(project.id), staleTime: 60_000 });
  const families = (reg.data?.families ?? []).filter((f) => f.installed);
  const family = mem.family && families.some((f) => f.id === mem.family) ? mem.family : families[0]?.id ?? "electronic";
  const calcs = (reg.data?.calculators ?? []).filter((c) => c.family === family);
  const calc: CalcDesc | undefined = calcs.find((c) => c.id === mem.calculator) ?? calcs[0];
  const values = useMemo(() => (calc ? { ...defaultInputs(calc), ...(mem.inputs?.[calc.id] ?? {}) } : {}), [calc, mem.inputs]);
  const setValues = (v: Record<string, unknown>) => calc && update({ inputs: { ...(mem.inputs ?? {}), [calc.id]: v } });
  const sent = calc ? changedInputs(calc, values) : {};
  const engine = calc ? chosenEngine(calc, reg.data, mem.engines?.[calc.id]) : "native";
  const spec = (inputs: Record<string, unknown>): CalcSpec[] =>
    calc ? [{ calculator: calc.id, inputs, ...(engine !== "native" ? { engine } : {}) }] : [];
  const [debounced] = useDebouncedValue(JSON.stringify(sent), 400);
  const check = useQuery({
    queryKey: ["properties-check", mem.model_file, calc?.id, engine, debounced],
    queryFn: () => propertiesCheck(mem.model_file!, spec(JSON.parse(debounced)), project.id),
    enabled: !!mem.model_file && !!calc,
  });
  const eligible = check.data?.eligible ?? {};
  const verdict = check.data?.calculations?.[0];
  const errorsOf = (id: string) => (eligible[id] ?? []).filter((i) => i.level === "error");
  const famAllowed = reg.data?.families.find((f) => f.id === family)?.allowed !== false;
  const problems = calc ? formProblems(calc, values) : [];
  if (!famAllowed) problems.push("This family of properties is not included in your plan.");
  if (verdict?.refused) problems.push(verdict.refused);

  const shownId = calc ? mem.prop_jobs?.[calc.id] : undefined;
  const { job } = useLiveJob(shownId);
  const run = useMutation({
    mutationFn: () => post<Job>("/jobs", { kind: `properties.${family}`, project_id: project.id,
      params: { model_file: mem.model_file, calculations: spec(sent) } }),
    onSuccess: (j) => {
      remember({ prop_jobs: { ...(mem.prop_jobs ?? {}), [calc!.id]: j.id } });
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => notifyError(e),
  });
  const result = job?.status === "succeeded" ? ((job.result?.results ?? [])[0] as PropertyResult | undefined) : undefined;

  if (reg.error) return <ErrorBox error={reg.error} />;
  return (
    <Grid mt="md" gutter="lg">
      <Grid.Col span={{ base: 12, md: 4 }}>
        <Stack gap="sm">
          {mem.preset && <PresetSteps preset={mem.preset} calcs={reg.data?.calculators ?? []} current={calc?.id}
            onPick={(id) => update({ family: id.split(".")[0], calculator: id })} />}
          <SegmentedControl fullWidth size="xs" value={family} data-testid="family"
            onChange={(v) => update({ family: v, calculator: (reg.data?.calculators ?? []).find((c) => c.family === v)?.id })}
            data={families.map((f) => ({ value: f.id, label: f.title }))} />
          {(reg.data?.families ?? []).some((f) => !f.installed) && (
            <Text size="xs" c="dimmed">Coming: {(reg.data?.families ?? []).filter((f) => !f.installed).map((f) => f.title.toLowerCase()).join(", ")}.</Text>
          )}
          <Stack gap={0} data-testid="calculators">
            {calcs.map((c) => {
              const errs = errorsOf(c.id);
              const item = (
                <NavLink key={c.id} label={c.title} active={c.id === calc?.id} disabled={errs.length > 0}
                  description={errs.length ? "not for this model" : undefined} onClick={() => update({ calculator: c.id })}
                  data-testid={`calc-${c.id}`} />
              );
              return errs.length ? (
                <Tooltip key={c.id} label={errs.map((e) => e.message + (e.fix ? ` ${e.fix}` : "")).join(" ")} multiline w={320}>
                  <div>{item}</div>
                </Tooltip>
              ) : item;
            })}
          </Stack>
          {calc && (
            <>
              <Text size="xs" c="dimmed">{calc.help}</Text>
              {calc.formula && <Formula tex={calc.formula} testId="formula" />}
              {calc.engines.length > 1 && (
                <Stack gap={2}>
                  <Text size="sm" fw={500}>Engine</Text>
                  <SegmentedControl size="xs" value={engine} data-testid="engine"
                    onChange={(v) => update({ engines: { ...(mem.engines ?? {}), [calc.id]: v } })}
                    data={engineChoices(calc, reg.data).map((c) => ({ value: c.value, disabled: c.disabled,
                      label: c.disabled ? <Tooltip label={c.reason} multiline w={280}><span>{c.label}</span></Tooltip> : c.label }))} />
                  {engine !== "native" && (
                    <Text size="xs" c="dimmed" data-testid="engine-note">{engineTitle(engine, reg.data)} runs on the model written as a
                      Wannier90 _tb.dat (the native gauge), for a cross-check of the native numbers; it cannot be stopped once it has
                      started, and inputs it has no counterpart for are reported in the result.</Text>
                  )}
                </Stack>
              )}
              <PropertyForm calc={calc} values={values} onChange={setValues} files={project.files ?? []} />
              {verdict?.cost && (
                <Text size="xs" c="dimmed" data-testid="cost">{verdict.cost.kpoints.toLocaleString()} k-points · {verdict.cost.matrix} orbitals ·
                  {" "}{fmtSeconds(verdict.cost.seconds)} (estimate)</Text>
              )}
              {(verdict?.issues ?? []).filter((i) => i.level === "warning").map((i) => (
                <Alert key={i.message} color="yellow" p="xs"><Text size="xs">{i.message}</Text></Alert>
              ))}
              {problems.map((p) => <Text key={p} size="sm" c="red">{p}</Text>)}
            </>
          )}
          <Group justify="space-between">
            <Button variant="default" onClick={onBack}>Back</Button>
            <Button onClick={() => run.mutate()} loading={run.isPending} disabled={!calc || !mem.model_file || problems.length > 0}>Run</Button>
          </Group>
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 8 }}>
        <RunCard job={job} exportName="properties.zip"
          extra={calc && <Group gap={4}><Badge variant="light">{calc.title}</Badge>
            {result && result.engine !== "native" && <Badge variant="outline">{engineTitle(result.engine, reg.data)}</Badge>}</Group>}
          empty={<Text c="dimmed" size="sm">Choose a property, set its inputs and press Run: the result appears here.</Text>}>
          {result && <PropertyResultView r={result} />}
        </RunCard>
        <PreviousRuns project={project} kind={`properties.${family}`} current={shownId} describe={describeProperties}
          onOpen={(id) => {
            const j = project.jobs.find((x) => x.id === id);
            const cid = ((j?.params as any)?.calculations?.[0]?.calculator as string | undefined) ?? calc?.id;
            if (cid) remember({ calculator: cid, prop_jobs: { ...(mem.prop_jobs ?? {}), [cid]: id } });
          }} />
      </Grid.Col>
    </Grid>
  );
}

/** A Properties project continuing an illustrative project: the calculations the example goes on with (their inputs are
 * already filled in), each one a click away. */
export function PresetSteps({ preset, calcs, current, onPick }: {
  preset: PropertiesPreset; calcs: CalcDesc[]; current?: string; onPick: (id: string) => void;
}) {
  const title = (id: string) => calcs.find((c) => c.id === id)?.title ?? id;
  const note = preset.calculations.find((c) => c.calculator === current)?.note;
  return (
    <Alert color="blue" p="xs" data-testid="preset">
      {preset.kind === "example"
        ? <Text size="sm">The calculations of <b>{preset.name}</b>, in order (their inputs are set):</Text>
        : <Text size="sm"><b>{preset.name}</b> continues here: {preset.what}.</Text>}
      <Group gap={6} mt={6}>
        {preset.calculations.map((c, i) => (
          <Button key={c.calculator} size="compact-xs" variant={c.calculator === current ? "filled" : "light"}
            onClick={() => onPick(c.calculator)} data-testid={`preset-${c.calculator}`}>{i + 1}. {title(c.calculator)}</Button>
        ))}
      </Group>
      {note && <Text size="xs" mt={6} data-testid="preset-note"><b>Look for:</b> {note}</Text>}
      {preset.kind !== "example" && preset.counterpart && (
        <Text size="xs" mt={6}>Guide and "Try this" variations:{" "}
          <Anchor component={Link} to={examplesPath("properties", preset.counterpart)}>the Properties illustrative project</Anchor>.</Text>
      )}
    </Alert>
  );
}
